/**
 * AreaCard — Groups line items by Area into a card with header + table.
 *
 * Per spec:
 * - Items grouped by Area into cards (each Area = own card with header + table)
 * - Per-area "+ Add ▾" floated right (Add line item · Add alternate)
 * - Area header shows area name + item count + area subtotal
 *
 * PR-02b: accessories are no longer siblings of line items — they nest under each
 * line item via CommittedLineCard's accessories tag row. The old `+ Add accessories`
 * menu row and `categoryId === 'accessories'` sub-section are removed.
 */

import React, { useState } from 'react';
import {
  PlusIcon,
  ChevronDownIcon,
  Bars3Icon,
  InformationCircleIcon,
} from '@heroicons/react/20/solid';
import { Card } from '../../ui/Card';
import DropdownMenu from '../../ui/DropdownMenu';
import CommittedLineCard from './CommittedLineCard';
import useDragReorder from '../../../hooks/useDragReorder';
import { deriveOptionLabel } from '../../../quote-builder/optionLabel';

export default function AreaCard({
  areaId,
  areaLabel,
  items = [],
  computedItems = [],
  overallMargin,
  onEditItem,
  onRemoveItem,
  onUpdateMargin,
  onUpdateQuantity,
  onAddItem,
  onAddAlternate,
  onRemoveOption,
  // PT-32 (2026-07-27) 32.2 + 32.3: reorder threading. `onReorderItem` handles
  // per-line drag WITHIN this area (via useDragReorder below); the area-level
  // props drive the keyboard-a11y Move up/down buttons in the card header.
  onReorderItem,
  canMoveAreaUp = false,
  canMoveAreaDown = false,
  onMoveAreaUp,
  onMoveAreaDown,
  // PT-45 (2026-07-29): optional per-item className callback for CO status styling.
  // Called with (item, idx) — returns a className string for the item wrapper div.
  getItemClassName,
  // PT-45: optional per-item overlay renderer (e.g., CO status badge).
  // Called with (item, idx) — returns a React node rendered after the line card.
  renderItemOverlay,
  // CO-03: optional per-item menu items callback. When provided, overrides the
  // default Edit/Delete kebab menu. Called with (item, idx) — returns an array
  // of { label, onClick, variant?, hidden? } objects.
  getMenuItems,
  // IDI-459: feature suppression by explicit PROP (ruling 09-10 — never a CSS
  // hide, never a fork). A change order is a diff of an existing job (ruling
  // 08-21), so:
  //   allowAlternates=false  → no "Add alternate" anywhere (E-31)
  //   allowPerLineAdd=false  → no per-area "+ Add" affordance (E-26)
  // The quote builder passes neither and is unchanged.
  allowAlternates = true,
  allowPerLineAdd = true,
}) {

  // Calculate area subtotal from computed items
  const areaSubtotal = computedItems.reduce((sum, ci) => sum + (ci?.total || 0), 0);

  // PT-32 (2026-07-27) 32.2: per-line drag primitive scoped to this area.
  // dragType='line-item' so drops on the area wrapper (which uses 'area')
  // and drops inside another area both silently no-op — the primitive rejects
  // cross-list payloads. `onReorderItem` is optional so legacy callers
  // (OptionsAreaCard renders without reorder for now) don't crash.
  const lineDragReorder = useDragReorder({
    itemCount: items.length,
    onReorder: (fromIdx, toIdx) => onReorderItem?.(fromIdx, toIdx),
    dragType: 'line-item',
    disabled: typeof onReorderItem !== 'function',
  });

  // PT-15 #2 (2026-07-24): Options area gets a specialised render — items are
  // grouped by optionId, each group rendered as an OPTION card with pill /
  // customer-facing description / own-margin badge / footnote. Base bid
  // subtotal excludes these (see QuoteBuilderPage.js summary computation).
  if (areaId === 'options') {
    return (
      <OptionsAreaCard
        items={items}
        computedItems={computedItems}
        onEditItem={onEditItem}
        onRemoveItem={onRemoveItem}
        onUpdateMargin={onUpdateMargin}
        onUpdateQuantity={onUpdateQuantity}
        onAddItem={onAddItem}
        onAddAlternate={onAddAlternate}
        onRemoveOption={onRemoveOption}
      />
    );
  }

  return (
    <Card className="overflow-visible">
      {/* PT-15 #4 (2026-07-24): overflow-hidden clipped the per-area "Add ▾"
          dropdown AND the CommittedLineCard ⋮ menu on bottom rows. Menus need
          to escape the card boundary. If sibling row transforms still clip,
          the fallback is portalling the menu — try overflow-visible first. */}
      {/* Area Header — §2 (2026-08-08): dark chrome treatment matching data
          tables + sidebar. All content on this bar uses high-contrast (white)
          tones since --color-chrome is #1F1F1F. */}
      <div
        className="flex items-center justify-between px-4 py-3"
        style={{ backgroundColor: 'var(--color-chrome)' }}
      >
        <div className="flex items-center gap-3">
          {(canMoveAreaUp || canMoveAreaDown) && (
            <div className="flex items-center" aria-label={`Reorder ${areaLabel} area`}>
              <span
                aria-hidden="true"
                className="cursor-grab active:cursor-grabbing p-1"
                title="Drag to reorder area"
                style={{ color: 'var(--sidebar-text-muted)' }}
              >
                <Bars3Icon className="h-4 w-4" />
              </span>
            </div>
          )}
          <h3
            className="text-sm font-semibold"
            style={{ color: 'var(--sidebar-text)', fontFamily: 'var(--font-display)' }}
          >
            {areaLabel}
          </h3>
          <span
            className="text-xs px-2 py-0.5 rounded-full"
            style={{ backgroundColor: 'var(--nav-active-bg)', color: 'var(--sidebar-text)' }}
          >
            {items.length} item{items.length !== 1 ? 's' : ''}
          </span>
        </div>

        <div className="flex items-center gap-3">
          {/* Area subtotal */}
          <span className="text-sm font-medium" style={{ color: 'var(--sidebar-text)' }}>
            ${(areaSubtotal / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </span>

          {/* Add — outlined variant for the dark bar so it doesn't punch a
              white hole. Border + text in a light tone; hover fills subtly.
              IDI-459 (E-26): suppressed by prop for change orders, which add
              lines from the single Add Item action instead of per-area. */}
          {allowPerLineAdd && (
          <DropdownMenu
            ariaLabel="Add to area"
            align="end"
            trigger={
              <button
                type="button"
                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded border transition-colors"
                style={{ borderColor: 'var(--sidebar-border)', color: 'var(--sidebar-text)', backgroundColor: 'transparent' }}
              >
                <PlusIcon className="h-3.5 w-3.5" />
                Add
                <ChevronDownIcon className="h-3.5 w-3.5" />
              </button>
            }
            panelClassName="w-40"
            items={[
              { label: 'Add line item', onClick: () => onAddItem(areaId) },
              // IDI-459 (E-31): alternates are a quote concept.
              ...(allowAlternates
                ? [{ label: 'Add alternate', onClick: () => onAddAlternate(areaId) }]
                : []),
            ]}
          />
          )}
        </div>
      </div>

      {/* Line Items — PR-22 (2026-07-23): container padding replaces the
          `divide-y` row treatment. Each CommittedLineCard is now a real card
          with its own border + `mb: 9px` (wire W4 `.lcard` token). */}
      <div className="p-3" style={{ paddingBottom: 0 }}>
        {items.map((item, idx) => {
          const itemIndex = idx;
          const canReorder = typeof onReorderItem === 'function';
          const isDragging = lineDragReorder.draggingIndex === idx;
          const isDropTarget = lineDragReorder.dropTargetIndex === idx;
          return (
            <div
              key={item.id}
              {...lineDragReorder.getItemProps(idx)}
              className={['flex items-start gap-1', getItemClassName ? getItemClassName(item, idx) : ''].filter(Boolean).join(' ')}
              style={{
                opacity: isDragging ? 0.4 : 1,
                outline: isDropTarget && !isDragging
                  ? '2px solid var(--color-action-primary)'
                  : 'none',
                outlineOffset: '1px',
                borderRadius: 'var(--radius-md)',
                transition: 'opacity 120ms ease',
              }}
            >
              {/* PT-32 32.2 + 32.6: per-line drag handle + keyboard Move up/down.
                  Only rendered when the parent supplies onReorderItem (feature
                  detection — keeps OptionsAreaCard's rendering path safe). */}
              {/* PT-53A W3.2 (DEF-051): chevrons removed. They were competing
                  with the drag handle for pointer events, and per operator
                  ruling they're only removed AFTER drag works (which it does
                  for areas, same hook). The drag handle (Bars3Icon) remains
                  as the sole reorder affordance. */}
              {/* §3 (2026-08-08): drag handle hides on single-item areas (nothing
                  to reorder), but the slot RESERVES its space so single-item cards
                  align with multi-item cards. Handle width = Bars3Icon h-3.5 (14px)
                  + p-0.5 (2px each side) = 18px + parent padding. */}
              <div
                className="flex flex-col items-center shrink-0 pt-3"
                style={{ color: 'var(--color-text-muted)', width: '18px' }}
              >
                {canReorder && items.length > 1 && (
                  <span
                    aria-hidden="true"
                    className="cursor-grab active:cursor-grabbing p-0.5"
                    title="Drag to reorder"
                    {...lineDragReorder.getHandleProps(idx)}
                  >
                    <Bars3Icon className="h-3.5 w-3.5" />
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <CommittedLineCard
                  item={item}
                  computed={computedItems[itemIndex]}
                  onEdit={() => onEditItem(item)}
                  onRemove={() => onRemoveItem(item.id)}
                  /* IDI-459 (E-31): suppressed by prop for change orders. */
                  onAddAlternate={allowAlternates && onAddAlternate ? () => onAddAlternate(item) : undefined}
                  onUpdateMargin={(margin) => onUpdateMargin(itemIndex, margin)}
                  onUpdateQuantity={onUpdateQuantity ? (q) => onUpdateQuantity(itemIndex, q) : undefined}
                  menuItems={getMenuItems ? getMenuItems(item, idx) : undefined}
                />
                {renderItemOverlay && renderItemOverlay(item, idx)}
              </div>
            </div>
          );
        })}

        {/* Empty state */}
        {items.length === 0 && (
          <div className="px-4 py-8 text-center">
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              No items in this area yet.
            </p>
            {/* IDI-459 (E-26): same suppression as the header affordance. */}
            {allowPerLineAdd && (
              <button
                type="button"
                onClick={() => onAddItem(areaId)}
                className="mt-2 inline-flex items-center gap-1 text-sm font-medium"
                style={{ color: 'var(--color-action-primary)' }}
              >
                <PlusIcon className="h-4 w-4" />
                Add your first line item
              </button>
            )}
          </div>
        )}
      </div>

      {/* PR-02b: legacy accessories sub-section removed. Accessories are now
          rendered as a tag row inside each CommittedLineCard. */}
    </Card>
  );
}

// ─── OptionsAreaCard ─────────────────────────────────────────────────────
// PT-15 #2 (2026-07-24)
//
// When AreaCard receives areaId === 'options', we group items by optionId
// and render each group as its own "OPTION" card with:
//   • OPTION pill using --color-status-option-* tokens (matches OPT badge
//     on QuoteDetail and elsewhere — no new/raw amber values)
//   • Header amount with `+$` prefix (positive delta over base bid)
//   • Own-margin badge inline in the header ("own margin 40%")
//   • Italic customer-facing description block
//   • Footnote: "Not included in the bid total. The customer accepts or
//     declines this separately." — matches the R&S "optional upgrades"
//     copy/math (see QuoteReviewSend `isOptionLine` predicate + docstring).
//   • Committed ⋮ menu: Remove (NO Edit — group is not authored per 361_ §2;
//     NO Add-alternative)
//   • Header label "Option N" is DERIVED at render time from the group's
//     first-occurrence position in the ordered set of option groups
//     (see quote-builder/optionLabel.js, PR-62 §347 §4 B11 N-2 §3au).
//     Never stored on the line. Reorder/delete renumbers automatically.
//
// Base bid exclusion: EstimatePanel/summary predicate lives upstream
// in QuoteBuilderPage (filters optionId-tagged lines out of the summary
// computation). Same predicate shape as R&S's isOptionLine.
function OptionsAreaCard({
  items,
  computedItems,
  onEditItem,
  onRemoveItem,
  onUpdateMargin,
  onUpdateQuantity,
  onAddAlternate,
  onRemoveOption,
}) {
  // Group by optionId. Lines with no optionId (legacy data) land in a
  // synthetic '__ungrouped__' bucket. PR-62 (363_ §1): group carries ONLY
  // optionId + its constituent lines. Name/description/margin are no longer
  // stored on the group — name is DERIVED at render (see deriveOptionLabel);
  // description is dropped entirely (§3ax / 361_ §1 Q1); margin is per-line
  // now (§3ak) and shown by each CommittedLineCard, not by the group header.
  const groups = [];
  const groupIndex = new Map();
  items.forEach((it, i) => {
    const key = it.optionId || '__ungrouped__';
    if (!groupIndex.has(key)) {
      groupIndex.set(key, groups.length);
      groups.push({
        optionId: key,
        items: [],
        computedItems: [],
        // area-relative indices — translate CommittedLineCard's
        // onUpdateMargin/Price/Quantity(localIdx, val) callbacks back to the
        // parent AreaCard's items[] index space.
        areaIndices: [],
      });
    }
    const g = groups[groupIndex.get(key)];
    g.items.push(it);
    g.computedItems.push(computedItems[i]);
    g.areaIndices.push(i);
  });

  // PR-62 N-2 (§3au): derive "Option N" labels from first-occurrence order.
  // We pass the ordered id list into each OptionGroupCard so it can render
  // its own position label without re-scanning the parent list.
  const optionGroupIds = groups
    .map((g) => g.optionId)
    .filter((oid) => oid !== '__ungrouped__');

  return (
    <div className="space-y-6">
      {groups.map((g) => (
        <OptionGroupCard
          key={g.optionId}
          group={g}
          optionGroupIds={optionGroupIds}
          onEditItem={onEditItem}
          onRemoveItem={onRemoveItem}
          onUpdateMargin={onUpdateMargin}
          onUpdateQuantity={onUpdateQuantity}
          onAddAlternate={onAddAlternate}
          onRemoveOption={onRemoveOption}
        />
      ))}
    </div>
  );
}

function OptionGroupCard({
  group,
  optionGroupIds,
  onEditItem,
  onRemoveItem,
  onUpdateMargin,
  onUpdateQuantity,
  onAddAlternate,
  onRemoveOption,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const total = group.computedItems.reduce((sum, ci) => sum + (ci?.total || 0), 0);

  // PR-62 N-2 (§3au): derived label. '__ungrouped__' returns '' — legacy
  // data lands in the same visual bucket but without a numeric header.
  const label = deriveOptionLabel(optionGroupIds, group.optionId);

  return (
    <Card className="overflow-visible">
      {/* Option header */}
      <div
        className="px-4 py-3 border-b"
        style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--neutral-50)' }}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {/* OPTION pill — uses --color-status-option-* token (same as OPT badge) */}
              <span
                className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wide"
                style={{
                  backgroundColor: 'var(--color-status-option-bg)',
                  color: 'var(--color-status-option-text)',
                }}
              >
                Option
              </span>
              <h3 className="text-base font-semibold text-gray-900 truncate">
                {label}
              </h3>
            </div>
            {/* PR-62 (§3ax / 361_ §1 Q1): option group has no description.
                The customer sees "Option N" and its constituent lines —
                meaning comes from the lines (via Alias §3aj when the catalog
                name is not offer-shaped). Description block removed. */}
          </div>
          <div className="flex items-start gap-3 flex-shrink-0">
            <div className="text-right">
              {/* §4 (2026-08-08): "Option total" label removed — the +$ is
                  self-explanatory; the label made the section read like a
                  running total the customer would owe. */}
              <div className="text-base font-semibold text-gray-900">
                +${(total / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
            {/* ⋮ menu — Remove only (§361_ §2: nothing about a group is
                authored, so there is no Edit surface. Remove is preserved
                because deleting a group is not editing it — it is the only
                way to atomically drop every line in the group with an Undo
                toast — see §363 §2.) */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setMenuOpen((v) => !v)}
                aria-label="Option actions"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                className="p-1 rounded hover:bg-gray-200"
                style={{ color: 'var(--color-text-secondary)' }}
              >
                <span className="text-lg leading-none">⋮</span>
              </button>
              {menuOpen && (
                <div
                  role="menu"
                  className="absolute right-0 top-full mt-1 z-30 min-w-[160px] rounded-md border py-1"
                  style={{
                    backgroundColor: 'var(--color-surface)',
                    borderColor: 'var(--color-border)',
                  }}
                >
                  <button
                    role="menuitem"
                    type="button"
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                    style={{ color: 'var(--color-danger)' }}
                    onClick={() => { setMenuOpen(false); onRemoveOption && onRemoveOption(group); }}
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Line items inside the option */}
      <div className="p-3 space-y-2">
        {group.items.map((item, idx) => {
          const areaIdx = group.areaIndices[idx];
          return (
            <CommittedLineCard
              key={item.id}
              item={item}
              computed={group.computedItems[idx]}
              onEdit={() => onEditItem(item)}
              onRemove={() => onRemoveItem(item.id)}
              onAddAlternate={onAddAlternate ? () => onAddAlternate(item) : undefined}
              onUpdateMargin={(margin) => onUpdateMargin(areaIdx, margin)}
              onUpdateQuantity={onUpdateQuantity ? (q) => onUpdateQuantity(areaIdx, q) : undefined}
            />
          );
        })}
      </div>

      {/* §4 (2026-08-08): footnote → info banner. Icon + tinted bg, no border-top
          (which read as a section divider instead of a note about this section). */}
      <div
        className="mx-3 mb-3 mt-1 px-3 py-2 flex items-start gap-2 rounded text-xs"
        style={{
          color: 'var(--color-text-default)',
          backgroundColor: 'var(--color-info-bg, var(--neutral-100))',
          border: '1px solid var(--color-info-border, var(--color-border))',
        }}
      >
        <InformationCircleIcon
          className="h-4 w-4 flex-shrink-0 mt-0.5"
          style={{ color: 'var(--color-text-muted)' }}
          aria-hidden="true"
        />
        <span>Not included in the bid total. The customer accepts or declines this separately.</span>
      </div>
    </Card>
  );
}
